/**
 * MarkdownContent — safe in-house markdown renderer for Grant Writer chat.
 *
 * 2026-02-12 · Founder Directive · Delivery Repair.
 * Renders substantive Grant Writer output as formatted headings / lists /
 * bold / italics / blockquotes / code blocks / tables. Everything is emitted
 * via React text nodes — NEVER `dangerouslySetInnerHTML`, so model output
 * cannot inject HTML/scripts. Intentionally small and dependency-free.
 */
import React from "react";

// -------- inline parsing (bold, italic, inline code) --------
// Returns an array of React nodes for one line of inline markdown.
function parseInline(text, keyPrefix = "") {
  if (!text) return [text];
  const nodes = [];
  // Order matters: inline code first (so its contents aren't further parsed),
  // then bold, then italic. Uses a single pass with regex indexOf loop.
  const patterns = [
    { re: /`([^`]+)`/, wrap: (m, i) => <code key={`${keyPrefix}c${i}`} className="px-1 py-0.5 bg-ink/5 rounded text-[0.85em] font-mono">{m[1]}</code> },
    { re: /\*\*([^*]+)\*\*/, wrap: (m, i) => <strong key={`${keyPrefix}b${i}`}>{m[1]}</strong> },
    { re: /__([^_]+)__/, wrap: (m, i) => <strong key={`${keyPrefix}u${i}`}>{m[1]}</strong> },
    { re: /\*([^*\n]+)\*/, wrap: (m, i) => <em key={`${keyPrefix}i${i}`}>{m[1]}</em> },
    { re: /_([^_\n]+)_/, wrap: (m, i) => <em key={`${keyPrefix}e${i}`}>{m[1]}</em> },
  ];

  let remaining = text;
  let counter = 0;
  // repeatedly find the earliest match across all patterns
  while (remaining.length) {
    let earliest = null;
    for (const p of patterns) {
      const m = p.re.exec(remaining);
      if (m && (earliest === null || m.index < earliest.match.index)) {
        earliest = { pattern: p, match: m };
      }
    }
    if (!earliest) {
      nodes.push(remaining);
      break;
    }
    if (earliest.match.index > 0) {
      nodes.push(remaining.slice(0, earliest.match.index));
    }
    nodes.push(earliest.pattern.wrap(earliest.match, counter++));
    remaining = remaining.slice(earliest.match.index + earliest.match[0].length);
  }
  return nodes;
}

// -------- block parsing --------
function renderBlocks(md) {
  const lines = (md || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  let blockKey = 0;

  const flushParagraph = (paraLines) => {
    if (!paraLines.length) return;
    const joined = paraLines.join(" ").trim();
    if (!joined) return;
    blocks.push(
      <p key={`p-${blockKey++}`} className="my-2 leading-relaxed">
        {parseInline(joined, `p${blockKey}-`)}
      </p>
    );
  };

  let paraBuffer = [];

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Blank line -> paragraph break
    if (trimmed === "") {
      flushParagraph(paraBuffer); paraBuffer = [];
      i++;
      continue;
    }

    // Horizontal rule ---
    if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) {
      flushParagraph(paraBuffer); paraBuffer = [];
      blocks.push(<hr key={`hr-${blockKey++}`} className="my-3 border-line" />);
      i++;
      continue;
    }

    // Heading  #  ##  ###  ####
    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushParagraph(paraBuffer); paraBuffer = [];
      const level = heading[1].length;
      const content = heading[2];
      const sizeCls = ["text-lg", "text-lg", "text-base", "text-sm", "text-sm", "text-sm"][level - 1];
      const Tag = `h${Math.min(level, 6)}`;
      blocks.push(
        React.createElement(
          Tag,
          {
            key: `h-${blockKey++}`,
            className: `${sizeCls} font-serif font-medium text-ink mt-3 mb-1.5 leading-tight`,
          },
          parseInline(content, `h${blockKey}-`)
        )
      );
      i++;
      continue;
    }

    // Code fence ```
    if (/^```/.test(trimmed)) {
      flushParagraph(paraBuffer); paraBuffer = [];
      const codeLines = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // skip closing fence
      blocks.push(
        <pre key={`code-${blockKey++}`} className="my-2 p-2 bg-ink/5 border border-line rounded overflow-x-auto text-[12px] font-mono leading-snug">
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // Blockquote  > ...
    if (/^>\s?/.test(trimmed)) {
      flushParagraph(paraBuffer); paraBuffer = [];
      const quoteLines = [];
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ""));
        i++;
      }
      blocks.push(
        <blockquote key={`bq-${blockKey++}`} className="my-2 pl-3 border-l-2 border-forest/40 italic text-ink-soft">
          {parseInline(quoteLines.join(" "), `bq${blockKey}-`)}
        </blockquote>
      );
      continue;
    }

    // Table row (heuristic: 2+ pipes in this line and next-line contains |---|)
    if (/^\|.*\|\s*$/.test(trimmed) && i + 1 < lines.length && /^\|\s*[-:| ]+\|\s*$/.test(lines[i + 1].trim())) {
      flushParagraph(paraBuffer); paraBuffer = [];
      const headerCells = trimmed.slice(1, -1).split("|").map(c => c.trim());
      i += 2; // skip header + separator
      const rows = [];
      while (i < lines.length && /^\|.*\|\s*$/.test(lines[i].trim())) {
        rows.push(lines[i].trim().slice(1, -1).split("|").map(c => c.trim()));
        i++;
      }
      blocks.push(
        <div key={`tw-${blockKey++}`} className="my-2 overflow-x-auto">
          <table className="text-xs border-collapse w-full">
            <thead>
              <tr>
                {headerCells.map((h, k) => (
                  <th key={k} className="text-left border border-line bg-bone/50 px-2 py-1 font-medium">
                    {parseInline(h, `th${k}-`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, ci) => (
                    <td key={ci} className="border border-line px-2 py-1 align-top">
                      {parseInline(c, `td${ri}-${ci}-`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Unordered list  - / *  +
    if (/^[-*+]\s+/.test(trimmed)) {
      flushParagraph(paraBuffer); paraBuffer = [];
      const items = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*+]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={`ul-${blockKey++}`} className="my-2 pl-5 list-disc space-y-1">
          {items.map((it, k) => (
            <li key={k} className="leading-relaxed">{parseInline(it, `ul${blockKey}-${k}-`)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list  1. / 2. / …
    if (/^\d+\.\s+/.test(trimmed)) {
      flushParagraph(paraBuffer); paraBuffer = [];
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push(
        <ol key={`ol-${blockKey++}`} className="my-2 pl-5 list-decimal space-y-1">
          {items.map((it, k) => (
            <li key={k} className="leading-relaxed">{parseInline(it, `ol${blockKey}-${k}-`)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Default: paragraph line
    paraBuffer.push(line);
    i++;
  }

  flushParagraph(paraBuffer);
  return blocks;
}

export default function MarkdownContent({ text, className = "" }) {
  return (
    <div className={`markdown-content ${className}`} data-testid="markdown-content">
      {renderBlocks(text)}
    </div>
  );
}
